import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, Alert, Spinner, Button } from 'react-bootstrap';
import { sendPayment } from '../../services/p2pAPI';
import { createBuyOrder } from '../../services/api';
import PaypalBridge from '../updated/BuySell/BuyCrypto/PaypalBridge';
import './P2PPayPalPayment.css';

const P2PPayPalPayment: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { tradeId, amount, price, totalAmount, cryptoType, paymentMethod } = location.state || {};
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [payPalLink, setPayPalLink] = useState('');

  useEffect(() => {
    if (!tradeId || !amount || !price) {
      setError('Missing trade information. Please go back and try again.');
    }
  }, [tradeId, amount, price]);

  const initiatePayPalPayment = async () => {
    if (!tradeId || !amount || !price) {
      setError('Missing trade information');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Create a buy order to get PayPal payment link (similar to regular Buy/Sell flow)
      const orderResponse = await createBuyOrder(
        cryptoType,
        'USD',
        totalAmount,
        amount,
        0,
        '',
        false,
        'Paypal'
      );

      if (orderResponse.status === 200 && orderResponse.data.links) {
        // Extract PayPal approval link
        let payPalPaymentLink = '';
        for (let i = 0; i < orderResponse.data.links.length; i++) {
          if (orderResponse.data.links[i].rel.includes('approve')) {
            payPalPaymentLink = orderResponse.data.links[i].href;
            break;
          }
        }

        if (payPalPaymentLink) {
          setPayPalLink(payPalPaymentLink);
        } else {
          setError('Failed to get PayPal payment link');
        }
      } else {
        setError('Failed to initiate PayPal payment');
      }
    } catch (err: any) {
      console.error('Failed to initiate PayPal payment:', err);
      setError(err.message || 'Failed to initiate PayPal payment');
    } finally {
      setLoading(false);
    }
  };

  if (!tradeId || !amount || !price) {
    return (
      <div className="p2p-payment-error">
        <Card>
          <Card.Body>
            <Alert variant="danger">
              <Alert.Heading>Error</Alert.Heading>
              <p>Missing trade information. Please go back and try again.</p>
              <Button onClick={() => navigate('/p2p/offers')}>
                Back to Offers
              </Button>
            </Alert>
          </Card.Body>
        </Card>
      </div>
    );
  }

  // If we have PayPal link, show the PayPal bridge component
  if (payPalLink) {
    return <PaypalBridge />;
  }

  return (
    <div className="p2p-paypal-payment">
      <div className="payment-container">
        <Card className="payment-card">
          <Card.Header>
            <h3>PayPal Payment</h3>
          </Card.Header>
          <Card.Body>
            {/* Trade Summary */}
            <div className="trade-summary">
              <h5>Trade Summary</h5>
              <div className="summary-row">
                <span>Cryptocurrency:</span>
                <strong>{cryptoType}</strong>
              </div>
              <div className="summary-row">
                <span>Amount:</span>
                <strong>{amount} {cryptoType}</strong>
              </div>
              <div className="summary-row">
                <span>Price per unit:</span>
                <strong>${price.toFixed(2)}</strong>
              </div>
              <div className="summary-row total-row">
                <span>Total Payment:</span>
                <strong>${totalAmount.toFixed(2)}</strong>
              </div>
            </div>

            {error && (
              <Alert variant="danger" className="mt-3">
                {error}
              </Alert>
            )}

            <div className="payment-instructions mt-4">
              <h5>Payment Instructions</h5>
              <ol>
                <li>Click "Proceed to PayPal" below</li>
                <li>Complete your PayPal payment</li>
                <li>Return to complete the trade</li>
              </ol>
            </div>

            <div className="payment-actions mt-4">
              <Button
                variant="primary"
                size="lg"
                onClick={initiatePayPalPayment}
                disabled={loading}
                className="w-100"
              >
                {loading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Initializing Payment...
                  </>
                ) : (
                  <>
                    <i className="fab fa-paypal me-2"></i>
                    Proceed to PayPal
                  </>
                )}
              </Button>

              <Button
                variant="outline-secondary"
                onClick={() => navigate('/p2p/offers')}
                className="w-100 mt-2"
              >
                Cancel
              </Button>
            </div>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default P2PPayPalPayment;
